import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import { WEB_LOGS_DEFAULTS, WEB_RUM_DEFAULTS } from '../config';
import { isCspViolationError } from '../helpers/isCspViolationError';

import type { ClientSpecificOverrides, StandardInitParams } from '../config';
import type { LogsInitConfiguration } from '@datadog/browser-logs';
import type { RumBeforeSend, RumInitConfiguration as WebConfigRaw } from '@datadog/browser-rum';

// ─────────────────────────────────────────────────────────────────────────────

export const init = async (options: StandardInitParams, overrides?: ClientSpecificOverrides) => {
  // ───────────────────────────────────
  // ── RUM ────────────────────────────
  const rumOptions = normalizeRumOptions(options, overrides?.rumWeb);

  datadogRum.init(rumOptions);

  // ───────────────────────────────────
  // ── LOGS ───────────────────────────

  const logsOptions = normalizeLogsOptions(options, overrides?.logsWeb);

  datadogLogs.init(logsOptions);

  // ───────────────────────────────────
  // ───────────────────────────────────

  // @REVIEW: revisit to see whether we want to enhance this in some way
  // eslint-disable-next-line unicorn/prefer-add-event-listener
  window.onerror = function (message, source, lineno, colno, error) {
    datadogLogs.logger.error(error?.message ?? '', {
      error: { stack: error?.stack },
    });
  };

  datadogLogs.logger.debug('Successfully ran `init` (web)');
};

// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// ── Normalize Helpers ────────────────────────────────────────────────────────

function normalizeRumOptions(
  standardInitParams: StandardInitParams,
  rumOverrides?: ClientSpecificOverrides['rumWeb'],
): WebConfigRaw {
  const optionsWithOverrides = {
    ...WEB_RUM_DEFAULTS,
    ...standardInitParams,
    ...rumOverrides,
    beforeSend: composeBeforeSend(dropCspViolations, rumOverrides?.beforeSend),
  };

  const withVersion = interpolateVersionSuffix(optionsWithOverrides);
  const withService = deAliasServiceName(withVersion);
  return withService as WebConfigRaw;
}

// ─────────────────────────

const dropCspViolations: RumBeforeSend = (event) => !isCspViolationError(event);

function composeBeforeSend(...beforeSends: (RumBeforeSend | undefined)[]): RumBeforeSend {
  return (event, context) =>
    beforeSends.every(
      (beforeSend) => beforeSend === undefined || beforeSend(event, context) !== false,
    );
}

// ─────────────────────────

function normalizeLogsOptions(
  standardInitParams: StandardInitParams,
  logsOverrides?: ClientSpecificOverrides['logsWeb'],
): LogsInitConfiguration {
  // appId doesn't apply to browser logs
  const { applicationId, ...standard } = standardInitParams; // eslint-disable-line @typescript-eslint/no-unused-vars

  const optionsWithOverrides = {
    ...WEB_LOGS_DEFAULTS,
    ...standard,
    ...logsOverrides,
  };

  const withVersion = interpolateVersionSuffix(optionsWithOverrides);
  const withService = deAliasServiceName(withVersion);
  return withService as LogsInitConfiguration;
}

// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// ── Utils ────────────────────────────────────────────────────────────────────

function interpolateVersionSuffix<T extends Record<string, any>>(options: T) {
  const { version: versionBase, versionSuffix, ...rest } = options;

  return {
    ...rest,
    version: versionSuffix ? `${versionBase}-${versionSuffix}` : versionBase,
  };
}
// ───────────────────────────

function deAliasServiceName<T extends Record<string, any>>(options: T) {
  const { serviceName, ...rest } = options;

  return { ...rest, service: serviceName };
}
